import logger from '../utils/logger';

import mockData from '../MockData/mockNearbyMachines.json';

// Env value (or default) with any trailing slash removed.
const envUrl = (value, fallback) => (value || fallback).replace(/\/+$/, '');

const API_BASE_URL = envUrl(process.env.REACT_APP_API_BASE_URL, 'https://api-prod.daalchini.co.in');
const IMAGE_BASE_URL = envUrl(
  process.env.REACT_APP_IMAGE_BASE_URL,
  'https://daalchini.s3.ap-south-1.amazonaws.com/dcprod'
);
const WEBAPP_BASE_URL = envUrl(process.env.REACT_APP_WEBAPP_BASE_URL, 'https://webapp.daalchini.co.in');

const MAIN_SITE_URL = 'https://www.daalchini.co.in/';
// Where users go when the Daalchini web app is down.
const FALLBACK_URL = process.env.REACT_APP_FALLBACK_URL || MAIN_SITE_URL;
// Where users go when the search API answers with anything but a successful 200.
const API_ERROR_REDIRECT_URL = process.env.REACT_APP_API_ERROR_REDIRECT_URL || MAIN_SITE_URL;

// The consumer backend rejects requests (400) without these; values match the Daalchini PWA.
const APP_HEADERS = { 'x-app-platform': 'web', 'x-app-version': 'v1.1.0' };

// Guest JWT from .env — the only token the app uses. It is never fetched or stored in the browser.
const GUEST_TOKEN = process.env.REACT_APP_GUEST_TOKEN?.trim() || '';

// Sends the user to the Daalchini website and keeps the page in its loading state
// while the browser navigates away.
function redirectToMainSite(reason) {
  logger.error('Outlet search failed:', reason, '- sending user to', API_ERROR_REDIRECT_URL);
  window.location.replace(API_ERROR_REDIRECT_URL);
  return new Promise(() => {});
}

// Only a successful 200 shows the list. Anything else (missing token, network failure,
// 401, 403, 429, 500, a malformed body…) sends the user to the Daalchini website instead
// of showing an error.
export async function fetchNearbyMachines({ lat, lng, limit = 50, signal }) {
  if (!GUEST_TOKEN) return redirectToMainSite('REACT_APP_GUEST_TOKEN is not set in .env');

  const params = new URLSearchParams({
    nearbyLimit: String(limit),
    recentLimit: '0',
    lat: String(lat),
    lng: String(lng),
  });

  let res;
  let body;
  try {
    res = await fetch(`${API_BASE_URL}/consumerbe/api/v2/outlet/search?${params}`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${GUEST_TOKEN}`, ...APP_HEADERS },
      signal,
    });
    body = res.status === 200 ? await res.json() : null;
  } catch (err) {
    // A cancelled request (the page unmounted or retried) is not a failure.
    if (err.name === 'AbortError') throw err;
    return redirectToMainSite(err);
  }

  if (res.status !== 200 || !body?.status?.success) {
    return redirectToMainSite(`status ${res.status} ${JSON.stringify(body?.status ?? null)}`);
  }
  return body?.data?.nearbyMachines || [];
}

// machineImagePath is relative to the S3 bucket, e.g. "VMLogo/Paytm_logo.png" or "/VM/image.png".
export function machineImageUrl(path) {
  if (!path) return null;
  return `${IMAGE_BASE_URL}/${encodeURI(path.replace(/^\/+/, ''))}`;
}

// The machine's item list on the Daalchini web app.
function machineItemsUrl(machineId) {
  const params = new URLSearchParams({ machineId: String(machineId), value: 'null', showItems: 'true' });
  return `${WEBAPP_BASE_URL}/items?${params}`;
}

const WEBAPP_CHECK_TIMEOUT_MS = 5000;

// Requests the web app's home page. It sends no CORS headers, so this is a `no-cors` request:
// the browser hides the status code, but the request only succeeds when the site answers.
// DNS failure, a refused connection or no answer within the timeout count as down.
async function isWebappLive() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), WEBAPP_CHECK_TIMEOUT_MS);
  try {
    await fetch(`${WEBAPP_BASE_URL}/`, { mode: 'no-cors', cache: 'no-store', signal: controller.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// Opens the machine's items in a new tab, or the Daalchini site if the web app is down.
// The tab is opened straight away (inside the click) so popup blockers allow it, then pointed
// at the right place once the check finishes.
export async function openMachineItems(machineId) {
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;

  const live = await isWebappLive();
  if (!live) logger.warn('Daalchini web app is not reachable; sending user to', FALLBACK_URL);
  const url = live ? machineItemsUrl(machineId) : FALLBACK_URL;

  if (tab && !tab.closed) tab.location.replace(url);
  else window.location.assign(url);
}

// distance comes back in metres.
export function formatDistance(metres) {
  if (metres == null) return '';
  return `${(metres / 1000).toFixed(1)} km`;
}
